/**
 * Fatiado de pages/Menu.tsx por responsabilidade (#131, R3).
 */
import { useState } from 'react'
import { currencyFor } from './shared.js'
import type { useQueryClient } from '@tanstack/react-query'
import { useMutation } from '@tanstack/react-query'
import { products as prodApi, type Product, type ProductVariant } from '../../lib/api.js'
import { Button, Input, toast } from '../../components/ui/index.js'
import { formatCents, parseCents } from '../../lib/utils.js'
import { useI18n } from '../../lib/i18n.js'
// ─── Variants editor ─────────────────────────────────────────────────────────

export function VariantsEditor({
  product,
  locale,
  currency = '',
  priceLocale = '',
  qc,
  onProductUpdate,
}: {
  product: Product
  locale: string
  /** #428: moeda do PAÍS do tenant; `currencyFor(locale)` é último recurso */
  currency?: string
  /** #428: locale de formatação Intl (língua base + país do restaurante) */
  priceLocale?: string
  qc: ReturnType<typeof useQueryClient>
  onProductUpdate: (p: Product) => void
}) {
  const { t } = useI18n()
  const [newName, setNewName] = useState('')
  const [newPrice, setNewPrice] = useState('')

  const addMut = useMutation({
    mutationFn: () =>
      prodApi.addVariant(product.id, {
        name: newName.trim(),
        price_cents: parseCents(newPrice),
        sort_order: product.variants.length,
      }),
    onSuccess: (variant) => {
      void qc.invalidateQueries({ queryKey: ['products'] })
      setNewName('')
      setNewPrice('')
      // #479: o base do produto é derivado das variantes — a resposta traz o
      // valor recalculado para o campo de preço (só de leitura) atualizar
      onProductUpdate({
        ...product,
        variants: [...product.variants, variant],
        price_cents: variant.product_price_cents,
      })
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const delMut = useMutation({
    mutationFn: (id: string) => prodApi.deleteVariant(product.id, id),
    onSuccess: (data, id) => {
      void qc.invalidateQueries({ queryKey: ['products'] })
      onProductUpdate({
        ...product,
        variants: product.variants.filter((v) => v.id !== id),
        price_cents: data.product_price_cents,
      })
    },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <div className="rounded-xl border border-gray-200 p-4">
      <p className="text-sm font-medium text-gray-700 mb-3">{t('admin.menu.variants')}</p>
      <div className="space-y-2 mb-3">
        {product.variants.length === 0 && (
          <p className="text-xs text-gray-400">{t('admin.menu.no_variants')}</p>
        )}
        {product.variants.map((v: ProductVariant) => (
          <div
            key={v.id}
            className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2"
          >
            <span className="text-sm">{v.name}</span>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-indigo-700">
                {formatCents(v.price_cents, currency || currencyFor(locale), priceLocale || locale)}
              </span>
              <button
                data-testid={`catalog-variant-delete-${v.id}`}
                onClick={() => delMut.mutate(v.id)}
                className="text-gray-400 hover:text-red-500 text-xs"
              >
                ✕
              </button>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <Input
          placeholder={t('admin.menu.variant_ph')}
          data-testid="catalog-variant-new-name"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          className="flex-1"
          onKeyDown={(e) => e.key === 'Enter' && newName.trim() && newPrice && addMut.mutate()}
        />
        <Input
          placeholder="29,90"
          data-testid="catalog-variant-new-price"
          value={newPrice}
          onChange={(e) => setNewPrice(e.target.value)}
          className="w-24"
        />
        <Button
          size="sm"
          variant="secondary"
          data-testid="catalog-variant-add"
          loading={addMut.isPending}
          onClick={() => addMut.mutate()}
          disabled={!newName.trim() || !newPrice}
        >
          +
        </Button>
      </div>
    </div>
  )
}
